/**
 * Canvas rendering utilities for tree visualization
 */

import type { TreeNode, Path, DrawOptions } from "./types";
import {
  COLORS,
  ALPHA,
  NODE_RADIUS,
  PATH_WIDTH,
  WAVE_FALLOFF_VH,
  FADE_DISTANCE_VH,
  GLOW_RADIUS,
} from "./constants";

// Pre-cached RGB color strings to avoid per-frame allocations
const RGB_ALIVE = `rgb(${COLORS.alive.r}, ${COLORS.alive.g}, ${COLORS.alive.b})`;
const RGB_DEAD = `rgb(${COLORS.dead.r}, ${COLORS.dead.g}, ${COLORS.dead.b})`;
const RGB_EMPTY = `rgb(${COLORS.empty.r}, ${COLORS.empty.g}, ${COLORS.empty.b})`;

function getRgbString(color: typeof COLORS[keyof typeof COLORS]): string {
  if (color === COLORS.alive) return RGB_ALIVE;
  if (color === COLORS.dead) return RGB_DEAD;
  return RGB_EMPTY;
}

// Wave state types for clarity
type WavePhase = 'before' | 'during' | 'after';

interface WaveMetrics {
  phase: WavePhase;
  proximity: number; // 0-1, how close to wave center
  distanceFromWave: number; // absolute pixels
  isInWaveRange: boolean;
  endFadeFactor: number; // 0-1, for final fade at top
}

/**
 * Analyzes element's relationship to the wave
 */
function getWaveMetrics(elementY: number, waveY: number, canvasHeight: number): WaveMetrics {
  const waveFalloff = (WAVE_FALLOFF_VH / 100) * canvasHeight;
  const distanceFromWave = Math.abs(waveY - elementY);
  const isInWaveRange = distanceFromWave < waveFalloff;
  
  // Determine phase
  let phase: WavePhase;
  if (waveY > elementY) {
    phase = 'before';
  } else if (isInWaveRange) {
    phase = 'during';
  } else {
    phase = 'after';
  }
  
  // Calculate proximity (1 when at wave center, 0 when at falloff distance)
  const proximity = isInWaveRange ? Math.max(0, 1 - distanceFromWave / waveFalloff) : 0;
  
  // Calculate end fade (when wave goes past the top)
  const waveAtEnd = waveY < 0;
  const endFadeFactor = waveAtEnd ? Math.max(0, 1 + waveY / waveFalloff) : 1;
  
  return {
    phase,
    proximity,
    distanceFromWave,
    isInWaveRange,
    endFadeFactor
  };
}

/**
 * Calculates visual properties for winner elements
 */
function getWinnerVisuals(
  metrics: WaveMetrics,
  isUltimateWinner: boolean,
  canvasHeight: number,
  elementY: number,
  waveY: number
): { color: typeof COLORS[keyof typeof COLORS], alpha: number, glow: number } {
  const fadeDistance = (FADE_DISTANCE_VH / 100) * canvasHeight;
  
  switch (metrics.phase) {
    case 'before':
      // Not yet reached by wave
      return { color: COLORS.empty, alpha: 0, glow: 0 };
      
    case 'during':
      // Wave is passing through - bright and glowing
      return {
        color: COLORS.alive,
        alpha: metrics.proximity * ALPHA.aliveWave + ALPHA.aliveBase,
        glow: metrics.proximity * GLOW_RADIUS
      };
      
    case 'after':
      // Wave has passed
      if (isUltimateWinner) {
        // Ultimate winners stay visible and gain glow as wave approaches top
        const glowIntensity = waveY > 0 ? Math.max(0, 1 - waveY / canvasHeight) : metrics.endFadeFactor;
        return {
          color: COLORS.alive,
          alpha: ALPHA.aliveBase * metrics.endFadeFactor,
          glow: GLOW_RADIUS * glowIntensity
        };
      } else {
        // Regular winners fade out after wave passes
        const distanceAfterWave = elementY - waveY;
        const fadeFactor = distanceAfterWave < fadeDistance 
          ? Math.max(0, 1 - distanceAfterWave / fadeDistance)
          : 0;
        return {
          color: COLORS.alive,
          alpha: ALPHA.aliveBase * fadeFactor,
          glow: 0
        };
      }
  }
}

/**
 * Calculates visual properties for loser elements
 */
function getLoserVisuals(
  metrics: WaveMetrics,
  canvasHeight: number,
  elementY: number,
  waveY: number
): { color: typeof COLORS[keyof typeof COLORS], alpha: number, glow: number } {
  const waveFalloff = (WAVE_FALLOFF_VH / 100) * canvasHeight;
  const fadeDistance = (FADE_DISTANCE_VH / 100) * canvasHeight;
  
  switch (metrics.phase) {
    case 'before':
      if (metrics.isInWaveRange) {
        // Wave approaching - fade in red
        return {
          color: COLORS.dead,
          alpha: ALPHA.dead * metrics.proximity,
          glow: 0
        };
      }
      return { color: COLORS.empty, alpha: 0, glow: 0 };
      
    case 'during':
      // Wave passing through - full red
      return {
        color: COLORS.dead,
        alpha: ALPHA.dead,
        glow: 0
      };
      
    case 'after':
      // Wave has passed - fade out
      const distanceAfterWave = elementY - waveY;
      
      if (distanceAfterWave < waveFalloff) {
        // Still close to wave - maintain visibility
        return {
          color: COLORS.dead,
          alpha: ALPHA.dead,
          glow: 0
        };
      } else if (distanceAfterWave < fadeDistance) {
        // Fading out
        const adjustedDistance = distanceAfterWave - waveFalloff;
        const adjustedFadeDistance = fadeDistance - waveFalloff;
        const fadeFactor = Math.max(0, 1 - adjustedDistance / adjustedFadeDistance);
        return {
          color: COLORS.dead,
          alpha: ALPHA.dead * fadeFactor,
          glow: 0
        };
      }
      // Fully faded
      return { color: COLORS.dead, alpha: 0, glow: 0 };
  }
}

/**
 * Calculates draw options for nodes
 */
export function calculateNodeDrawOptions(
  node: TreeNode,
  waveY: number,
  isWinner: boolean,
  isUltimateWinner: boolean,
  canvasHeight: number
): DrawOptions {
  const metrics = getWaveMetrics(node.y, waveY, canvasHeight);
  
  // Get base visual properties
  const visuals = isWinner 
    ? getWinnerVisuals(metrics, isUltimateWinner, canvasHeight, node.y, waveY)
    : getLoserVisuals(metrics, canvasHeight, node.y, waveY);
  
  // Apply node-specific alpha adjustments
  const nodeAlpha = visuals.alpha > 0 
    ? visuals.alpha * (ALPHA.nodeAliveBase / ALPHA.aliveBase) // Scale to node alpha range
    : 0;
  
  // Calculate radius based on wave proximity
  let radius: number = NODE_RADIUS.base;
  if (isWinner) {
    if (metrics.isInWaveRange) {
      radius = NODE_RADIUS.base + metrics.proximity * NODE_RADIUS.wave;
    } else if (metrics.phase === 'after') {
      radius = NODE_RADIUS.active;
    }
  }
  
  return {
    color: visuals.color,
    alpha: nodeAlpha,
    radius,
    glow: visuals.glow
  };
}

/**
 * Calculates draw options for paths
 */
export function calculatePathDrawOptions(
  avgY: number,
  waveY: number,
  isWinner: boolean,
  isUltimateWinner: boolean,
  canvasHeight: number
): DrawOptions {
  const metrics = getWaveMetrics(avgY, waveY, canvasHeight);
  
  // Get base visual properties
  const visuals = isWinner 
    ? getWinnerVisuals(metrics, isUltimateWinner, canvasHeight, avgY, waveY)
    : getLoserVisuals(metrics, canvasHeight, avgY, waveY);
  
  // Calculate line width based on wave proximity
  let lineWidth: number = PATH_WIDTH.base;
  if (isWinner) {
    if (metrics.isInWaveRange) {
      lineWidth = PATH_WIDTH.base + metrics.proximity * PATH_WIDTH.wave;
    } else if (metrics.phase === 'after') {
      lineWidth = PATH_WIDTH.active;
    }
  }
  
  return {
    color: visuals.color,
    alpha: visuals.alpha,
    lineWidth,
    glow: visuals.glow
  };
}

/**
 * Legacy function for backward compatibility
 */
export function calculateDrawOptions(
  elementY: number,
  waveY: number,
  isWinner: boolean,
  isUltimateWinner: boolean,
  canvasHeight: number,
  isNode: boolean = false
): DrawOptions {
  if (isNode) {
    const node = { y: elementY } as TreeNode;
    return calculateNodeDrawOptions(node, waveY, isWinner, isUltimateWinner, canvasHeight);
  } else {
    return calculatePathDrawOptions(elementY, waveY, isWinner, isUltimateWinner, canvasHeight);
  }
}

/**
 * Calculates RGBA color string based on element state and wave position
 * Using the same logic as calculateDrawOptions for consistency
 */
export function calculateColorString(
  elementY: number,
  waveY: number,
  isWinner: boolean,
  isUltimateWinner: boolean,
  canvasHeight: number
): string {
  const options = calculateDrawOptions(
    elementY,
    waveY,
    isWinner,
    isUltimateWinner,
    canvasHeight,
    false
  );
  const { color, alpha } = options;
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${alpha})`;
}

/**
 * Draws a path between parent and child nodes
 */
export function drawPath(
  ctx: CanvasRenderingContext2D,
  path: Path,
  isWinner: boolean,
  isUltimateWinner: boolean,
  waveY: number,
  canvasHeight: number
): void {
  const { parent, child, midY } = path;
  const avgY = (parent.y + child.y) / 2;

  // Get drawing options for this path
  const options = calculatePathDrawOptions(
    avgY,
    waveY,
    isWinner,
    isUltimateWinner,
    canvasHeight
  );

  // Skip if completely transparent
  if (options.alpha === 0) return;

  // Apply drawing options using cached RGB string + globalAlpha
  ctx.strokeStyle = getRgbString(options.color);
  ctx.globalAlpha = options.alpha;
  ctx.lineWidth = options.lineWidth || PATH_WIDTH.base;

  // Apply glow effect if needed
  let needsResetShadow = false;
  if (options.glow && options.glow > 0) {
    ctx.shadowColor = getRgbString(options.color);
    ctx.shadowBlur = options.glow;
    needsResetShadow = true;
  }

  // Draw path with right angles
  ctx.beginPath();
  ctx.moveTo(child.x, child.y);
  ctx.lineTo(child.x, midY);
  ctx.lineTo(parent.x, midY);
  ctx.lineTo(parent.x, parent.y);
  ctx.stroke();

  // Reset state
  ctx.globalAlpha = 1;
  if (needsResetShadow) {
    ctx.shadowBlur = 0;
    ctx.shadowColor = "transparent";
  }
}

/**
 * Draws a node circle
 */
export function drawNode(
  ctx: CanvasRenderingContext2D,
  node: TreeNode,
  isWinner: boolean,
  isUltimateWinner: boolean,
  waveY: number,
  canvasHeight: number
): void {
  const options = calculateNodeDrawOptions(
    node,
    waveY,
    isWinner,
    isUltimateWinner,
    canvasHeight
  );

  // Skip if completely transparent
  if (options.alpha === 0) return;

  // Apply drawing options
  const needsResetShadow = applyDrawOptions(ctx, options);

  // Draw node circle
  ctx.beginPath();
  ctx.arc(node.x, node.y, options.radius || NODE_RADIUS.base, 0, Math.PI * 2);
  ctx.fill();

  // Reset state
  ctx.globalAlpha = 1;
  if (needsResetShadow) {
    ctx.shadowBlur = 0;
    ctx.shadowColor = "transparent";
  }
}

/**
 * Applies draw options to canvas context
 * Returns true if shadow was set (needs reset)
 */
function applyDrawOptions(
  ctx: CanvasRenderingContext2D,
  options: DrawOptions
): boolean {
  const { color, alpha, lineWidth, glow } = options;
  const rgbString = getRgbString(color);

  ctx.globalAlpha = alpha;
  if (lineWidth !== undefined) {
    ctx.strokeStyle = rgbString;
    ctx.lineWidth = lineWidth;
  } else {
    ctx.fillStyle = rgbString;
  }

  if (glow && glow > 0) {
    ctx.shadowColor = rgbString;
    ctx.shadowBlur = glow;
    return true;
  }
  return false;
}

/**
 * Clears the entire canvas
 */
export function clearCanvas(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  ctx.clearRect(0, 0, width, height);
}

/**
 * Sets up canvas with proper DPI scaling
 */
export function setupCanvas(
  canvas: HTMLCanvasElement,
  width: number,
  height: number
): CanvasRenderingContext2D | null {
  // Cap DPR to 1 for performance - background animation doesn't need retina sharpness
  const dpr = Math.min(window.devicePixelRatio || 1, 1);

  // Set actual canvas size accounting for DPI
  canvas.width = width * dpr;
  canvas.height = height * dpr;

  // Don't set explicit style dimensions - let CSS handle the display size
  // This allows the canvas to remain responsive
  // canvas.style.width = `${width}px`;
  // canvas.style.height = `${height}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) {
    console.error("Failed to get 2D context");
    return null;
  }

  // Scale for DPI
  ctx.scale(dpr, dpr);

  return ctx;
}

/**
 * Optimized batch rendering of all elements
 */
export function renderFrame(
  ctx: CanvasRenderingContext2D,
  nodes: TreeNode[],
  paths: Path[],
  winners: Map<string, boolean>,
  ultimateWinners: Set<string>,
  waveY: number,
  width: number,
  height: number
): void {
  // Clear canvas
  clearCanvas(ctx, width, height);

  // Save context state
  ctx.save();

  // Draw paths first (behind nodes)
  paths.forEach((path) => {
    const isWinner = winners.get(path.id) ?? false;
    const isUltimateWinner = ultimateWinners.has(path.id);
    drawPath(ctx, path, isWinner, isUltimateWinner, waveY, height);
  });

  // Draw nodes on top
  nodes.forEach((node) => {
    const isWinner = winners.get(node.id) ?? false;
    const isUltimateWinner = ultimateWinners.has(node.id);
    drawNode(ctx, node, isWinner, isUltimateWinner, waveY, height);
  });

  // Restore context state
  ctx.restore();
}
